// app/javascript/pages/Admin/TestGenerator/titleHelper.ts

/**
 * Escapes regex special characters in a string.
 */
function escapeRegExp(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/**
 * Strips duplicate exam names or codes from the start of a test type name.
 * E.g., given exam "SBI Clerk 2026", "SBI Clerk: Reasoning Ability Sectional" -> "Reasoning Ability Sectional"
 * or "SBI Clerk Prelims Full Mock" -> "Prelims Full Mock"
 */
export function cleanTypeName(typeName: string, examName?: string, examCode?: string): string {
  if (!typeName) return ''
  let clean = typeName.trim()

  if (examName) {
    const baseExam = examName.replace(/\s*\b(19|20)\d{2}\b/g, '').trim()
    const pattern = new RegExp(`^(${escapeRegExp(examName)}|${escapeRegExp(baseExam)})\\s*[:\\-–—]?\\s*`, 'i')
    clean = clean.replace(pattern, '').trim()
  }

  if (examCode) {
    const pattern = new RegExp(`^${escapeRegExp(examCode)}\\s*[:\\-–—]?\\s*`, 'i')
    clean = clean.replace(pattern, '').trim()
  }

  return clean
}

/**
 * Maps standard test type keys to clean human-readable labels.
 */
export function getFallbackTypeLabel(testTypeKey?: string, syllabusName?: string): string {
  if (!testTypeKey) return 'Mock Test'

  const key = testTypeKey.toLowerCase()
  if (key.includes('reasoning')) return 'Reasoning Ability Sectional'
  if (key.includes('quant') || key.includes('math') || key.includes('numerical')) return 'Numerical Ability Sectional'
  if (key.includes('english')) return 'English Language Sectional'
  if (key.includes('general_awareness') || key.includes('gk') || key.includes('ga')) return 'General Awareness Sectional'

  switch (key) {
    case 'full_mock':
      return 'Full Mock'
    case 'sectional':
      return 'Sectional Test'
    case 'chapter_test':
      return syllabusName ? `${syllabusName} Chapter Practice` : 'Chapter Practice'
    case 'pyq_paper':
      return 'PYQ Paper'
    case 'practice':
      return 'Practice Set'
    case 'speed_drill':
    case 'speed_quiz':
      return 'Speed Quiz'
    case 'custom':
      return 'Custom Test'
    default: {
      // Convert snake_case or kebab-case to Title Case
      return testTypeKey
        .replace(/[_-]/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase())
    }
  }
}

interface BuildTitleOptions {
  examName?: string
  examCode?: string
  testTypeName?: string
  testTypeKey?: string
  syllabusName?: string
  questionCount?: number
  numberSuffix?: number
}

/**
 * Builds a clean, professional, non-duplicated test title.
 * e.g., "SBI Clerk 2026 Reasoning Ability Sectional 1"
 * or "SBI Clerk 2026 Prelims Full Mock 1"
 */
export function buildDefaultTestTitle(options: BuildTitleOptions): string {
  const {
    examName,
    examCode,
    testTypeName,
    testTypeKey,
    syllabusName,
    numberSuffix = 1,
  } = options

  const baseExam = (examName || examCode || '').trim()

  let effectiveTypeLabel = ''
  if (testTypeName) {
    effectiveTypeLabel = cleanTypeName(testTypeName, examName, examCode)
  }

  if (!effectiveTypeLabel) {
    effectiveTypeLabel = getFallbackTypeLabel(testTypeKey, syllabusName)
  }

  if (!baseExam && !effectiveTypeLabel) {
    return `Mock Test ${numberSuffix}`
  }

  if (!baseExam) {
    return /\s+\d+$/.test(effectiveTypeLabel) ? effectiveTypeLabel : `${effectiveTypeLabel} ${numberSuffix}`
  }

  if (!effectiveTypeLabel) {
    return `${baseExam} Test ${numberSuffix}`
  }

  // If the effective type label already ends with a number (e.g. "Mock 1" or "Test 2"), don't append another number
  if (/\s+\d+$/.test(effectiveTypeLabel)) {
    return `${baseExam} ${effectiveTypeLabel}`
  }

  return `${baseExam} ${effectiveTypeLabel} ${numberSuffix}`
}

/**
 * Checks if a title appears auto-generated or has repetitive duplications.
 * E.g., "SBI Clerk 2026 SBI Clerk Prelims Full Mock 1" has duplicate "SBI Clerk".
 */
export function hasDuplicateExamWords(title: string, examName?: string): boolean {
  if (!title || !examName) return false
  const baseExam = examName.replace(/\s*\b(19|20)\d{2}\b/g, '').trim().toLowerCase()
  if (!baseExam || baseExam.length < 3) return false

  // Count occurrences of baseExam in lowercase title
  const lowerTitle = title.toLowerCase()
  const firstIdx = lowerTitle.indexOf(baseExam)
  if (firstIdx !== -1) {
    const secondIdx = lowerTitle.indexOf(baseExam, firstIdx + baseExam.length)
    if (secondIdx !== -1) return true
  }
  return false
}

/**
 * Determines if the current title appears auto-generated (meaning safe to update when switching configs)
 */
export function isAutoGeneratedTitle(title?: string, examName?: string, examCode?: string): boolean {
  if (!title || !title.trim()) return true
  if (hasDuplicateExamWords(title, examName)) return true
  // Default titles end with numbers like 1, or generic labels
  if (/(\bMock Test|\bTest|\bMock|\bDrill|\bPaper|\bSet|\bSectional|\bChapter)\s+\d+$/i.test(title)) return true
  return false
}

